/**
 * DERAG3R2Service — Geocodificador L1.5 para infraestructura hidráulica
 *
 * Busca infraestructuras DERA G3 (manantiales, captaciones, depósitos,
 * estaciones de aforo) almacenadas en R2 mediante fuzzy matching por nombre.
 *
 * Datos: DERA G3 (4 capas puntuales) — ~3.500 features
 * CRS: EPSG:25830 (UTM30 ETRS89)
 * Ruta R2: /dera-g3/{ine5}.json.gz
 * Precisión: ±10 m (coordenadas oficiales DERA)
 *
 * @module services/geocoding/specialized
 * @version 1.0.0
 * @date 2026-02-16
 */

import uFuzzy from '@leeoniya/ufuzzy';
import type {
  DERAG3Feature,
  DERAG3SearchOptions,
  DERAG3Result,
} from '../../../types/deraG3';
import { G3_X_RANGE, G3_Y_RANGE, G3_PRECISION_M } from '../../../types/deraG3';
import type { GeocodingResult } from '../../../types/infrastructure';
import { fetchWithBackoff } from '../../../utils/fetchWithBackoff';
import { WORKER_BASE } from '@/lib/config/workerEndpoints';

// =============================================================================
// CONSTANTES
// =============================================================================

/**
 * URL base del Worker R2 (alias local).
 * Fuente única de verdad: `@/lib/config/workerEndpoints` (`WORKER_BASE`).
 */
const WORKER_BASE_URL = WORKER_BASE;
const FETCH_TIMEOUT = 15_000;
const CACHE_MAX_ITEMS = 50;

/** Configuración uFuzzy para nombres españoles de infraestructura */
const UFUZZY_OPTIONS: uFuzzy.Options = {
  intraMode: 1,
  intraIns: 1,
  intraSub: 1,
  intraTrn: 1,
  intraDel: 1,
  interIns: 6,
};

// =============================================================================
// SINGLETON
// =============================================================================

let serviceInstance: DERAG3R2Service | null = null;

export function getDERAG3R2Service(): DERAG3R2Service {
  if (!serviceInstance) {
    serviceInstance = new DERAG3R2Service();
  }
  return serviceInstance;
}

/** Reset singleton (útil para tests) */
export function resetDERAG3R2Service(): void {
  if (serviceInstance) {
    serviceInstance.clearCache();
  }
  serviceInstance = null;
}

// =============================================================================
// CLASE PRINCIPAL
// =============================================================================

export class DERAG3R2Service {
  private readonly baseUrl: string;
  private readonly cache = new Map<string, { data: DERAG3Feature[]; ts: number }>();
  private readonly loadingPromises = new Map<string, Promise<DERAG3Feature[]>>();
  private readonly fuzzy: uFuzzy;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl ?? WORKER_BASE_URL;
    this.fuzzy = new uFuzzy(UFUZZY_OPTIONS);
  }

  // ---------------------------------------------------------------------------
  // CARGA DE DATOS POR MUNICIPIO
  // ---------------------------------------------------------------------------

  /** Carga features G3 de un municipio desde R2 (con cache LRU) */
  async loadMunicipio(codINE: string): Promise<DERAG3Feature[]> {
    if (!/^\d{5}$/.test(codINE)) return [];

    const cached = this.cache.get(codINE);
    if (cached) return cached.data;

    const pending = this.loadingPromises.get(codINE);
    if (pending) return pending;

    const promise = this.fetchMunicipio(codINE);
    this.loadingPromises.set(codINE, promise);

    try {
      const features = await promise;
      this.setCache(codINE, features);
      return features;
    } finally {
      this.loadingPromises.delete(codINE);
    }
  }

  private async fetchMunicipio(codINE: string): Promise<DERAG3Feature[]> {
    const url = `${this.baseUrl}/dera-g3/${codINE}.json.gz`;

    try {
      const { response } = await fetchWithBackoff(url, { timeoutMs: FETCH_TIMEOUT });

      if (response.status === 404) return [];
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data: DERAG3Feature[] = await response.json();
      return data.filter(f => this.isValidCoord(f));
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        console.warn(`[DERAG3] Timeout cargando ${codINE}`);
      } else {
        console.error(`[DERAG3] Error cargando ${codINE}:`, error);
      }
      return [];
    }
  }

  // ---------------------------------------------------------------------------
  // GEOCODIFICACIÓN
  // ---------------------------------------------------------------------------

  async geocode(options: DERAG3SearchOptions): Promise<GeocodingResult | null> {
    const results = await this.search(options);
    if (results.length === 0) return null;
    return this.toGeocodingResult(results[0], options.name);
  }

  async search(options: DERAG3SearchOptions): Promise<DERAG3Result[]> {
    const { codINE, name, subtipo, minConfidence = 60, maxResults = 5 } = options;

    const features = await this.loadMunicipio(codINE);
    if (features.length === 0) return [];

    let candidates = features;
    if (subtipo) {
      candidates = candidates.filter(
        f => f.subtipo.toUpperCase() === subtipo.toUpperCase()
      );
      if (candidates.length === 0) return [];
    }

    if (!name?.trim()) {
      return candidates
        .slice(0, maxResults)
        .map(f => this.toResult(f, 70));
    }

    return this.fuzzySearch(candidates, name, minConfidence, maxResults);
  }

  // ---------------------------------------------------------------------------
  // FUZZY MATCHING
  // ---------------------------------------------------------------------------

  private fuzzySearch(
    candidates: DERAG3Feature[],
    searchName: string,
    minConfidence: number,
    maxResults: number,
  ): DERAG3Result[] {
    const haystack = candidates.map(f => f.nombre);
    const normalizedName = searchName.toLowerCase().trim();
    const searchResult = this.fuzzy.search(haystack, normalizedName);

    if (!searchResult?.[0] || searchResult[0].length === 0) return [];

    const [idxs, _info, order] = searchResult;
    const processOrder = order ?? idxs;
    const results: DERAG3Result[] = [];

    for (let i = 0; i < Math.min(processOrder.length, maxResults * 2); i++) {
      const idx = processOrder[i];
      const featureIdx = idxs[idx];
      const feature = candidates[featureIdx];
      if (!feature) continue;

      const fuzzyScore = this.calcFuzzyScore(normalizedName, feature.nombre);
      const confidence = this.fuzzyScoreToConfidence(fuzzyScore);

      if (confidence >= minConfidence) {
        results.push(this.toResult(feature, confidence, fuzzyScore));
      }
      if (results.length >= maxResults) break;
    }

    return results;
  }

  private calcFuzzyScore(needle: string, haystack: string): number {
    const hLow = haystack.toLowerCase();
    if (hLow === needle) return 1.0;
    if (hLow.includes(needle)) return 0.95;
    if (needle.includes(hLow)) return 0.90;
    const ratio = needle.length / Math.max(hLow.length, needle.length);
    return Math.min(0.85, 0.5 + ratio * 0.4);
  }

  private fuzzyScoreToConfidence(score: number): number {
    if (score >= 0.95) return 95;
    if (score >= 0.85) return 88;
    if (score >= 0.70) return 78;
    if (score >= 0.55) return 68;
    if (score >= 0.40) return 58;
    return 50;
  }

  // ---------------------------------------------------------------------------
  // CONVERSIÓN
  // ---------------------------------------------------------------------------

  private toResult(
    feature: DERAG3Feature,
    confidence: number,
    fuzzyScore?: number,
  ): DERAG3Result {
    return {
      x: feature.x,
      y: feature.y,
      confidence,
      precision: G3_PRECISION_M,
      source: 'dera_g3_r2',
      feature,
      fuzzyScore,
    };
  }

  toGeocodingResult(result: DERAG3Result, originalQuery?: string): GeocodingResult {
    const f = result.feature;
    return {
      x: result.x,
      y: result.y,
      confidence: result.confidence,
      source: 'dera_g3_r2',
      sourceLayer: f.capa,
      originalQuery,
      matchedName: f.nombre,
      matchType: result.fuzzyScore != null ? 'fuzzy' : 'filter',
      fuzzyScore: result.fuzzyScore,
      municipality: f.municipio,
      muniCode: f.cod_ine,
      province: f.provincia,
      metadata: {
        id: f.id,
        subtipo: f.subtipo,
        capa: f.capa,
        precision: result.precision,
        fuente: 'DERA G3 Infraestructura Hidráulica',
        ...(f.meta ?? {}),
      },
    };
  }

  // ---------------------------------------------------------------------------
  // UTILIDADES
  // ---------------------------------------------------------------------------

  private isValidCoord(f: DERAG3Feature): boolean {
    return (
      f.x >= G3_X_RANGE.min && f.x <= G3_X_RANGE.max &&
      f.y >= G3_Y_RANGE.min && f.y <= G3_Y_RANGE.max
    );
  }

  private setCache(codINE: string, data: DERAG3Feature[]): void {
    if (this.cache.size >= CACHE_MAX_ITEMS) {
      const oldest = this.cache.keys().next().value;
      if (oldest) this.cache.delete(oldest);
    }
    this.cache.set(codINE, { data, ts: Date.now() });
  }

  clearCache(): void {
    this.cache.clear();
    this.loadingPromises.clear();
  }

  /** Carga datos directamente (para tests sin fetch) */
  loadFromData(codINE: string, features: DERAG3Feature[]): void {
    const valid = features.filter(f => this.isValidCoord(f));
    this.cache.set(codINE, { data: valid, ts: Date.now() });
  }

  get cacheSize(): number { return this.cache.size; }
}

export default DERAG3R2Service;
